<template>
  <table class="nutrition-facts">
    <tbody>
      <tr>
        <td>
          <permissions-control-wrapper
             v-if="showDownload"
             action="permissions.subscribed_plan.nutrition_label_printing"
            :required-general="$PERMISSIONS_GENERAL.DOWNLOAD"
            :required-subscribed-plan="$PERMISSIONS_SUBSCRIBED_PLAN.NUTRITION_LABEL_PRINTING"
            :permissions="permissions">
              <div slot="allowed" class="nf-labels-dropdown">
                <a class="button button--more-less is-small is-warning" @click="handleDownloadPDF">
                  <span class="more">
                    {{ $t('label.labels') }} <i class="far fa-angle-right" style="margin-left: 0.25em"></i>
                  </span>
                </a>
              </div>

              <div slot="not-allowed" class="nf-labels-dropdown">
                <a class="button button--more-less is-small is-warning is-disabled">
                  <span class="more">
                    {{ $t('label.labels') }} <i class="far fa-angle-down"></i>
                  </span>
                </a>
              </div>
            </div>
          </permissions-control-wrapper>
          <div class="nf-main-info no-border-bottom">
            <h4 class="nf-title">{{ $t('label.nutrition_facts') }}</h4>
          </div>
        </td>
      </tr>
      <tr>
        <td>
          <div v-if="mode === '100'" class="nf-main-info">
            <h5 class="nf-serving-size">{{ $t('label.serving_size') }} <span>100g</span></h5>
          </div>
          <div v-if="mode === 'quantity'" class="nf-main-info">
            <h5 class="nf-serving-size">{{ $t('label.quantity') }} <span>{{ selectedServing.weight_gr }}g</span></h5>
          </div>
          <div v-if="mode === 'select-serving-size'" class="nf-main-info">
            <h5 class="nf-serving-size">{{ $t('label.serving_size') }}</h5>
            <div class="custom-select">
              <select v-model="predictServingSizeID">
                <optgroup v-for="group in availableServing" :label="group.title" :key="group.key">
                  <option v-for="serving in group.list" :value="serving.id" :selected="serving.id === predictServingSizeID">{{ serving.title || serving.description }} ({{ serving.weight_gr }}g)</option>
                </optgroup>
              </select>
            </div>
          </div>
        </td>
      </tr>
      <tr>
        <td>
          <div id="nf-energy-info-region" class="nf-energy-info">
            <div class="nf-border double-line-bottom">
              <!-- European label format -->
              <template v-if="_get(productNutritionFacts, 'meta.label_format') === NUTRITION_FACTS_LABEL_FORMAT.EUROPEAN">
                <h5 class="nf-title">
                  <span class="nf-label">{{ _get(energyList, 'name') }}</span>
                  <span class="nf-value">
                    <template v-for="(nutrientDefinition, index) in energyList.values">
                      <span :class="nutrientDefinition.values[0].unit">
                        <template v-if="nutritionInformation[nutrientDefinition.code].defined">
                          {{ nutritionInformation[nutrientDefinition.code].value | number-format(0) }} {{ nutrientDefinition.values[0].unit }}
                        </template>
                        <template v-else>~</template>
                      </span>
                      <span v-if="index === 0"> / </span>
                    </template>
                  </span>
                </h5>
              </template>

              <!-- USA label format -->
              <template v-else-if="_get(productNutritionFacts, 'meta.label_format') === NUTRITION_FACTS_LABEL_FORMAT.AMERICAN">
                <h5 class="nf-title is-us-label-format">
                  <span class="nf-label">{{ _get(energyList, 'us_title') }}</span>
                  <span class="nf-value">
                    <span :class="kcal">{{ nutritionInformation['ENERC_KCAL'].value | number-format(0) }}</span>
                  </span>
                </h5>
              </template>
            </div>
          </div>
        </td>
      </tr>
      <tr>
        <td>
          <div id="nf-top-ten-region" class="nf-top-ten">
            <ul class="nf-top-list">
              <li class="nf-header">
                <span class="nf-label">{{ $t('label.amount') }}</span>
                <span class="nf-value">{{ $t('label.daily_value') }}</span>
              </li>
              <li class="nf-top-content">
                <ul>
                  <li v-for="topTenGroup in topTenList.items">
                    <span class="nf-label">
                      <span>{{ getItemLabel(topTenGroup) }}</span>
                      <template v-if="nutritionInformation[topTenGroup.code].defined">
                        {{ nutritionInformation[topTenGroup.code].value | number-format(2, 10) }}{{ topTenGroup.values[0].unit }}
                      </template>
                      <template v-else>~</template>
                    </span>
                     <span
                         class="nf-value is-have-title"
                        :title="_get(topTenGroup, 'values.1.quantity') !== '' ? $t('label.daily_recommended_intake', { quantity: _get(topTenGroup, 'values.1.quantity'), unit: _get(topTenGroup, 'values.1.unit') }) : $t('label.daily_recommended_intake_not_defined')">
                        <template v-if="nutritionInformation[topTenGroup.code].defined">
                          {{ (nutritionInformation[topTenGroup.code].value * 100 / topTenGroup.values[1].quantity) | number-format(1,1) | sufix-if-number('%') }}
                        </template>
                        <template v-else>~</template>
                     </span>
                    <ul>
                      <li v-for="topTenItem in topTenGroup.children">
                        <span class="nf-label">
                          <span>{{ getItemLabel(topTenItem) }}</span>
                          <template v-if="nutritionInformation[topTenItem.code].defined">
                            {{ nutritionInformation[topTenItem.code].value | number-format(2, 10) }}{{ topTenItem.values[0].unit }}
                          </template>
                          <template v-else>~</template>
                        </span>
                        <span
                           class="nf-value gr-value is-have-title"
                          :title="_get(topTenItem, 'values.1.quantity') !== '' ? $t('label.daily_recommended_intake', { quantity: _get(topTenItem, 'values.1.quantity'), unit: _get(topTenItem, 'values.1.unit') }) : $t('label.daily_recommended_intake_not_defined')">
                          <template v-if="nutritionInformation[topTenItem.code].defined">
                            {{ (nutritionInformation[topTenItem.code].value * 100 / topTenItem.values[1].quantity) | number-format(1,1) | sufix-if-number('%') }}
                          </template>
                          <template v-else>~</template>
                        </span>
                      </li>
                    </ul>
                  </li>
                </ul>
              </li>
            </ul>
        </div>
        </td>
      </tr>
    </tbody>
    <tfoot>
      <tr>
        <td class="double-line-top">
          <div class="nf-important" :class="{ 'all-items': showAllImportant }">
            <div id="nf-important-region">
              <div class="nf-important-list">
                <template v-for="children in importantList.children">
                  <template v-for="important in children.items">
                    <span class="important-item" :class="{ 'is-essential': children.list === 'essential' }">
                      <strong>{{ getItemLabel(important) }}</strong>
                      <template v-if="nutritionInformation[important.code].defined">
                          {{ nutritionInformation[important.code].value | number-format(2, 1) }}{{ important.values[0].unit }} <span class="is-have-title" :title="_get(important, 'values.1.quantity') !== '' ? $t('label.daily_recommended_intake', { quantity: _get(important, 'values.1.quantity'), unit: _get(important, 'values.1.unit') }) : $t('label.daily_recommended_intake_not_defined')">{{ (nutritionInformation[important.code].value * 100 / important.values[1].quantity) | number-format(1,1) | sufix-if-number('%') }}</span>
                      </template>
                      <template v-else>~</template>
                    </span>
                    <span v-if="isNotLast(children.items, important)" class="dot" :class="{ 'is-essential': children.list === 'essential' }"> &#8226; </span>
                  </template>
                  <span v-if="isNotLast(importantList.children, children)" class="dot"> &#8226; </span>
                </template>
              </div>
            </div>
            <div class="more-less">
              <a @click="toggleShowImportant" class="button button--more-less is-small is-warning">
                <span class="more">{{ $t('label.more') }}</span>
                <span class="less">{{ $t('label.less') }}</span>
              </a>
            </div>
          </div>
        </td>
      </tr>
      <tr>
        <td class="thin-line-bottom">
          <div id="nf-cal-per-gram-region" class="nf-cal-per-gram">
            <div class="nf-cal-per-gram">
              <nuttrema-nutritions-stucture-bars
                :nutritions="gangOfFourList">
              </nuttrema-nutritions-stucture-bars>
            </div>
          </div>
        </td>
      </tr>
      <tr>
        <td>
          <p class="nf-note">
            <template v-if="isKnownLifeStage">
              * {{ $t('label.daily_value_text_personal') }}
            </template>
            <template v-else>
              * {{ $t('label.daily_value_text_default') }}
            </template>
          </p>
        </td>
      </tr>
    </tfoot>
  </table>
</template>

<script>
import { each } from 'lodash';

import NutritionFacts from './nutrition-facts';

export default {
  name: 'nutrition-facts-realtime',
  mixins: [NutritionFacts],
  props: {
    nutritionInformation: {
      required: true,
      default: null,
    },
  },
  computed: {
    gangOfFourList() {
      const gang = {};
      const four = ['PROCNT', 'CHOCDF', 'FAT', 'ALC'];
      each(four, (one) => {
        gang[one] = {
          name: '',
          nutrition_value: Number(this.nutritionInformation[one].value),
        };
      });
      return gang;
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/ui/nutrition-information/nutrition-facts-realtime.vue